import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, OnInit, Output, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { ChecklistGroup, ChecklistItem, ChecklistResponse, DownloadFormPayload, DownloadFormPayloadItem, PDFDiffChanges } from '../../../interfaces/checklist.model';
import { PDFFormConfig } from '../../../interfaces/config';
import { CheckListService } from '../../../services/check-list.service';
import { ReferralDataService } from '../../../services/referral-data.service';
import { ButtonComponent } from '@ppl/ui';
import { ListTableComponent } from '@ppl/ui';
import { IC_PROVIDER_CLASSIFICATION_FORM, VE_PROVIDER_CLASSIFICATION_FORM, PROVIDER_TYPES, RADIO_YES_NO_OPTIONS, STATIC_JSON_DATA_PATH } from '../../../shared/constants/static-data.constants';
import { EditpdfComponent } from "../../editpdf/editpdf.component";
import { ChecklistHelperService } from '../../../services/checklist.helper.service';
import { EnrollmentDataService } from '../../../services/enrollment-data.service';
import { ProviderEnrollmentFormsComponent } from '../provider-enrollment-forms/provider-enrollment-forms.component';
import { SectionsService } from '../../../services/sections.service';
import { LoggerService } from '../../../shared/services/logger.service';
import { Subject } from 'rxjs';
import { PopupDialogComponent } from '@ppl/ui';
import { DialogConfig, DialogService } from '../../../shared/services/dialog.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { GridAction } from '@ppl/ui';
import { MailFormService } from '../../../services/mail-form.service';
import { SendMailFormPostItem } from '../../../interfaces/mailform.interface';
import { UserService } from '../../../services/user.service';
import { StorageService } from '../../../services/storage.service';
import { ConsumerRateService } from '../../../services/consumer-rater.service.';
import { DateTimeFormatterService } from '../../../shared/utils/date-time-formatter.service';
import { environment } from 'src/environments/environment';
import { stripLegacyTableCellKeys } from '../../../shared/constants/pdf-table.constants';

@Component({
  selector: 'ppl-provider-forms',
  standalone: true,
  imports: [
    CommonModule,
    NzAlertModule,
    ButtonComponent,
    NzCollapseModule,
    NzRadioModule,
    NzInputModule,
    NzSelectModule,
    NzDatePickerModule,
    EditpdfComponent,
    ListTableComponent,
    PopupDialogComponent,
    ProviderEnrollmentFormsComponent
  ],
  providers: [CheckListService],
  templateUrl: './provider-forms.component.html',
  styleUrl: './provider-forms.component.css'
})
export class ProviderFormComponent implements OnInit {
  private dialogSvc = inject(DialogService);
  private dateTimeFormatterService = inject(DateTimeFormatterService);

  /** Form IDs that require service code prepopulation */
  private readonly SERVICE_CODE_FORM_IDS = [101, 108];
  private readonly SERVICE_NAME_FIELD_LABELS = ['Service Name and Service Code'];
  private readonly HOURLY_AMOUNT_FIELD_LABELS = ['Hourly Wage', 'Hourly Rate'];
  private readonly HCBS_ENCLOSURE_B_FORM_IDS = [110, 122, 128];
  private providerEnrollmentFlags: {
    servicesAtBusinessLocation?: string | null;
    servingIndividualsWithDisabilities?: string | null;
  } | null = null;
  private pdfConfigRequestToken = 0;
  private hasNoRates = false;
  @Input() readonly?: boolean = false;
  @Input() isNewReferral = false;
  @Output() isMandatoryFormsCompleted = new EventEmitter<boolean>();
  @ViewChild('associationsDialog') associationDialogRef!: TemplateRef<any>;
  @ViewChild('pdfDialog') pdfDialogRef!: TemplateRef<any>;
  @ViewChild('confirmationDialog2') confirmationDialog2!: TemplateRef<any>;
  @ViewChild('formHistoryDialog') formHistoryDialogRef!: TemplateRef<any>;
  @ViewChild('showChangesDialog') showChangesDialogRef!: TemplateRef<any>;

  @ViewChild(EditpdfComponent) viewEditPDFComponent!: EditpdfComponent;

  showConfirmationDialog = false;

  selectedItemToMail: WritableSignal<ChecklistItem | null> = signal(null);

  signForm = new Subject<object>();

  prvdrAssociations: any = [];
  associationDetailsData: any = {};
  // basicInfo/contactDetails from getAssociationDetails(), used to prefill the W-9's
  // Legal Name of Business, DBA, Mailing Address and SSN/EIN fields.
  providerAssociationDetails: any = null;
  private providerIdByPplId: Record<string, string> = {};

  date = '';
  radioOptions = RADIO_YES_NO_OPTIONS;
  showModal = false;
  showAssociationModal = false;
  modalBody = {
    associationModal: '',
    pdfViewer: ''
  };
  selectedForm: any;
  selectedAssociationForm: any;
  allPanelsExpanded = false;
  pdfConfig?: PDFFormConfig;
  previousPdfConfig?: PDFFormConfig
  pdfFile?: string;
  pdfBlob?: Blob;
  qrCode?: any;
  checkListData: ChecklistGroup[] = [];
  providerAssociationChecklist?: ChecklistGroup[] = [];
  formData: any;
  program: any;
  disableSave = true;
  disableSign = true;
  errors: any = [];
  signature: any;
  signatureId: any;
  isSignAdded = false;
  checklistResponse?: ChecklistResponse;
  isParticipantFlow = false;
  userId = '';
  source = '';
  pplId = '';
  entry: string | null = '';
  cols: any[] = [];
  associatedPPLId?: string;
  selectedParticipantId?: number;
  selectedParticipantPplId?: string;
  selectedProviderType?: string;
  isSelf: boolean = true;
  selectedParticipant: any;
  selectedProviderId: string = '';

  // ── Employer ⇄ Provider forms accordions ──
  // Employer (EMP) → "Checklist for Providers" (EMPLOYERPROVIDER, userType EMP).
  // Independent Provider (IR) → "Checklist for Employers" (PROVIDEREMPLOYER, userType IR).
  // The linked forms render through the existing #associationsDialog (ppl-provider-enrollment-forms),
  // so viewing a form goes through the same getFormData/PDF path as participant associations.
  isEmployerProvider = false;
  isIndependentProvider = false;
  employerProviderList: any[] = [];
  providerEmployerList: any[] = [];
  selectedLinkedRow: any;
  selectedLinkedMode: 'employerProvider' | 'providerEmployer' | null = null;
  employerProviderCols: any[] = [
    { field: 'prvName', header: 'Provider Name', jsonpath: 'prvName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'prvPplId', header: 'Provider PPL ID', jsonpath: 'prvPplId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'empName', header: 'Employer', jsonpath: 'empName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'empPplId', header: 'Employer PPL ID', jsonpath: 'empPplId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  ];
  
  providerEmployerCols: any[] = [
    { field: 'empName', header: 'Employer Name', jsonpath: 'empName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'empPplId', header: 'Employer PPL ID', jsonpath: 'empPplId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'prvName', header: 'Provider', jsonpath: 'prvName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'prvPplId', header: 'Provider PPL ID', jsonpath: 'prvPplId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  ];

  relationToParticipant: string = '';
  customError = '';
  actions: any;
  isPdfSubmitted: boolean = false;
  isI9Form: boolean = false;
  resignRequired = true;
  // Scenario 5: block setting the I-9 citizenship to U.S. citizen while the Tax Exemptions
  // non-resident-alien option is still selected (the provider must update that form first).
  private readonly I9_CITIZENSHIP_FIELD = 'Citizenship or immigration status';
  private readonly I9_US_CITIZEN_VALUE = 'Citizen';
  private readonly TAX_EXEMPTIONS_FORM_ID = 81;
  private readonly NON_RESIDENT_ALIEN_KEY = 'Non-resident Alien';
  private taxHasNonResidentAlien = false;
  private hasPersistedPdfChanges = false;
  isMobile = false;
  isAdmin = false;
  actionType = '';
  isHistory = false;
  isHistoryAssociation = false;
  private readonlyBeforeHistory = false;
  private providerTypeBeforeHistory?: string;
  activePanel: any;
  diffChanges?: string;
  highlightedFields: PDFDiffChanges[] = [];
  formHistoryChecklist: ChecklistGroup[] = [];
  canAllowFormEditing = false;
  historyCols: any = [
    {
      "field": "createdBy",
      "header": "Last Updated By",
      "jsonpath": "createdBy",
      "isDisplayedAtHeader": "y",
      "isSearchable": "n"
    },
    {
      "field": "createdDate",
      "header": "Date Updated",
      "jsonpath": "createdDate",
      "isDisplayedAtHeader": "y",
      "isSearchable": "n"
    },
    {
      "field": "status",
      "header": "Form Status",
      "jsonpath": "status",
      "isDisplayedAtHeader": "y",
      "isSearchable": "n"
    }
  ];

  summaryCols: any = [
    {
      "field": "path",
      "header": "Field",
      "jsonpath": "path",
      "isDisplayedAtHeader": "y",
      "isSearchable": "n"
    },
    {
      "field": "value",
      "header": "Original Value",
      "jsonpath": "value",
      "isDisplayedAtHeader": "y",
      "isSearchable": "n"
    },
    {
      "field": "currentValue",
      "header": "New Value",
      "jsonpath": "currentValue",
      "isDisplayedAtHeader": "y",
      "isSearchable": "n"
    }
  ]

  participantAssociationActions: any = {
    "view": true
  }

  downloadAll() {
    this.handleDownload(this.checklistResponse?.checkList || [], false, this.pplId);
  }

  getCombinedChecklistName(): string {
    return this.checkListData.map(cl => cl.checklistName).join(' + ');
  }

  getCombinedTotalCompleted(): number {
    return this.checkListData.reduce((sum, cl) => sum + cl.totalItemsCompleted, 0);
  }

  getCombinedTotalItems(): number {
    return this.checkListData.reduce((sum, cl) => sum + cl.totalItems, 0);
  }

  isCombinedChecklistCompleted(): boolean {
    return this.checkListData.every(cl => cl.isCompleted);
  }

  constructor(
    private checkListService: CheckListService,
    private referralService: ReferralDataService,
    private sectionsService: SectionsService,
    private logger: LoggerService,
    private checklistHelperService: ChecklistHelperService,
    private enrollmentDataService: EnrollmentDataService,
    private notificationService: NotificationService,
    private mailFormService: MailFormService,
    private userService: UserService,
    private storageService: StorageService,
    private consumerRateService: ConsumerRateService
  ) {

  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth < 768;
    const userDetails = this.userService.getUserDetails();
    if (!userDetails) {
      console.error('no user details found');
      return;
    }
    this.isAdmin = userDetails.usertype_lkp == 'FMS';
    this.actions = {
      "edit": true,
      "download": true,
      "mail": !this.readonly,
    }
    this.entry = this.userService.getLeadEntry() ?? '';
    this.program = this.userService.getSelectedProgram();
    this.userId = this.storageService.getParticipantId() || this.storageService.getProviderId() || this.userService.getSelectedLead() || '';
    this.isParticipantFlow = this.userService.getLeadEntry() == 'participantEnrollment' || this.sectionsService.getFlow() == 'participantEnrollment';
    this.source = this.isParticipantFlow ? 'participant' : 'provider';
    this.loadData();
    this.getSignature(this.program.program_code, this.source, this.userId);
  }

  loadData(forceFresh = false) {
    this.checkListService.getChecklist(this.program?.program_code, this.userId || '', this.isParticipantFlow, forceFresh).subscribe((data: any) => {
      if (data.responsedata) {
        this.checklistResponse = data.responsedata;
        this.checklistResponse?.checkList.forEach((checklist: ChecklistItem) => {
          this.applyPendingResignState(checklist);
        });
        this.checklistResponse?.checkList.forEach((checklist: any) => {
          checklist['arialabel'] = checklist.formDesc + 'Status ' + (checklist.formStatus == 'C' ? 'Completed' : 'Not Completed');
        });
        if (this.checklistResponse) {
          this.pplId = this.checklistResponse.pplId;
          this.rebuildChecklistData();

          if (this.isParticipantFlow) {
            this.updateMandatoryFormsCompleteStatus();
          } else {
            // Load the Employer ⇄ Provider linked lists once per checklist load (not on every
            // rebuild — the association-load rebuild would otherwise re-fire the list calls).
            this.resolveSelfTypesAndLoadLinkedLists(forceFresh);
          }
        }
      }
    });

    if (!this.isParticipantFlow) {
      this.getGridHeaders();
      this.getAssociationDetails();
      this.enrollmentDataService.getParticipantAssocChanged().subscribe((data: any) => {
        this.getAssociationDetails();
      })
    } else {
      this.loadProviderIdMap();
    }
  }

  async getGridHeaders() {
    try {
      if (this.entry) {
        const tempCols = await this.sectionsService.getJsonConfigurationData(
          this.entry === 'participantEnrollment' ? STATIC_JSON_DATA_PATH.ASSOCIATIONS_DETAILS_HEADERS : STATIC_JSON_DATA_PATH.PROVIDER_ASSOCIATIONS_HEADERS, this.entry
        );
        // tempCols.columns.push({
        //   "field": "formStatusText",
        //   "header": "Form Status",
        //   "jsonpath": "formStatusText",
        //   "isDisplayedAtHeader": "y",
        //   "isSearchable": "n"
        // });
        console.log(tempCols.columns);
        this.cols = tempCols.columns;
      }
    } catch (e) {
      this.logger.debug('Communication logs - Error in getting column data', e);
    }
  }

  handleSign() {
    if (!this.disableSign) {
      this.signForm.next({
        isParticipant: this.isParticipantFlow,
        participantId: this.selectedParticipantId,
        providerType: this.selectedProviderType
      });
    }
    this.disableSign = true;
  }

  getSignature(program_code: string, type: string, id: string | null) {
    this.referralService.getSignature(program_code, type, id).subscribe((data: any) => {
      if (data?.responsedata && data?.responsedata.length) {
        const activeSign = data?.responsedata.find((data: any) => data.isActive);
        this.isSignAdded = true;
        this.signature = `data:image/png;base64,${activeSign?.signatureImageBase64}`;
        this.disableSign = false;
        this.signatureId = this.isParticipantFlow ? activeSign.participantSignatureId : activeSign.providerSignatureId;
      }
    })
  }

  loadProviderIdMap() {
    this.enrollmentDataService.getAssociationsDetails(this.program.program_code, this.userId, true).subscribe((response: any) => {
      const data = response.responsedata;
      this.providerIdByPplId = {};
      for (const key in PROVIDER_TYPES) {
        const providers: any[] = data[key] || [];
        providers.forEach((p: any) => {
          if (p.pplProviderId && p.providerId) {
            this.providerIdByPplId[p.pplProviderId] = p.providerId;
          }
        });
      }
    });
  }

  getAssociationDetails() {
    const participantId = !this.isParticipantFlow ? this.storageService.getSelfEmployerParticipantId() : undefined;
    this.enrollmentDataService.getAssociationsDetails(this.program.program_code, this.userId, this.isParticipantFlow, false, participantId).subscribe((response: any) => {
      const data = response.responsedata;
      this.providerAssociationDetails = data;
      if (this.isParticipantFlow) {
        for (const key in PROVIDER_TYPES) {
          this.associationDetailsData[key] = data[key] || [];
        }
      } else {
        this.prvdrAssociations = data.participantAssociations ?? [];
        this.prvdrAssociations.forEach((association: any) => {
          association.participantName = association.participantFirstName + ' ' + association.participantLastName;
        });
        this.providerEnrollmentFlags = {
          servicesAtBusinessLocation: data?.basicInfo?.servicesAtBusinessLocation,
          servingIndividualsWithDisabilities: data?.basicInfo?.servingIndividualsWithDisabilities,
        };
        this.rebuildChecklistData();
      }

      // for (const checklist of this.prvdrAssociations) {
      //   if (checklist.formStatus == 'C') {
      //     checklist.formStatusText = "Completed"
      //   } else {
      //     checklist.formStatusText = "Not Completed"
      //   }
      // }

      this.prvdrAssociations = this.prvdrAssociations.filter((association: any) => association.associationStatus != 'Disassociated');
      this.pplId = data?.basicInfo?.providerPPLId || this.pplId;
      // if (!this.isParticipantFlow) {
      //   const hasEINAvailableForEmployer = data?.basicInfo?.identifiers?.some((e: any) => e.isActive && e.type == 'EIN') || false;
      //   this.storageService.setHasEINAvaialableForEmployer(hasEINAvailableForEmployer);
      // }

      this.updateMandatoryFormsCompleteStatus();
    });
  }

  private rebuildChecklistData(): void {
    if (!this.checklistResponse) return;

    const sourceList = this.filterHiddenForms(this.checklistResponse.checkList);

    if (this.isParticipantFlow) {
      this.checkListData = this.checklistHelperService.buildChecklistDataForParticipant(sourceList, true);
    } else {
      this.checkListData = this.checklistHelperService.buildChecklistDataForProvider(sourceList, null, true, true);
    }
  }

  // ── Employer ⇄ Provider checklist accordions ────────────────────────────────
  private resolveSelfTypesAndLoadLinkedLists(forceFresh = false) {
    const selfTypes = new Set<string>();
    this.collectSelfTypes(this.checkListData, selfTypes);
    this.isEmployerProvider = selfTypes.has('EMP');
    this.isIndependentProvider = selfTypes.has('IR');

    if (this.isEmployerProvider) {
      this.checkListService.getEmployerProviderList(this.program?.program_code, this.userId || '', forceFresh).subscribe((res: any) => {
        this.employerProviderList = Array.isArray(res?.responsedata) ? res.responsedata : [];
      });
    }
    if (this.isIndependentProvider) {
      this.checkListService.getProviderEmployerList(this.program?.program_code, this.userId || '', forceFresh).subscribe((res: any) => {
        this.providerEmployerList = Array.isArray(res?.responsedata) ? res.responsedata : [];
      });
    }
  }

  private collectSelfTypes(groups: ChecklistGroup[] | undefined, acc: Set<string>) {
    for (const g of groups ?? []) {
      for (const item of g.items ?? []) {
        (item.details ?? []).forEach((d: any) => {
          if (d.associationId == -1 && d.type) acc.add(d.type);
        });
      }
      this.collectSelfTypes(g.children, acc);
    }
  }

  employerProviderAction(event: { action: string; data: any }) {
    if (event.action === 'view') this.openLinkedForms('employerProvider', event.data);
  }

  providerEmployerAction(event: { action: string; data: any }) {
    if (event.action === 'view') this.openLinkedForms('providerEmployer', event.data);
  }

  // Open the form-view wizard (#associationsDialog / ppl-provider-enrollment-forms) for the linked
  // provider or employer. Viewing a form there goes through the existing getFormData/PDF path.
  openLinkedForms(mode: 'employerProvider' | 'providerEmployer', row: any) {
    this.selectedLinkedMode = mode;
    this.selectedLinkedRow = row;
    const isEmp = mode === 'employerProvider';

    // Forms belong to the "other" entity in the row: the provider (employer view) or the employer (IP view).
    this.selectedProviderId = String(isEmp ? row.prvId : row.empId);
    this.selectedParticipantPplId = row.ptcPplId;
    this.selectedParticipantId = undefined;
    // Signer type = the current user: employer signs the provider's form ('EMP'), provider signs
    // the employer's form ('IR'). 'EMP' also lets the wizard open the sign PDF without questionnaire
    // validation (onAction), and drives the correct signature placement in editpdf.
    this.selectedProviderType = isEmp ? 'EMP' : 'IR';
    this.modalBody.associationModal = isEmp
      ? 'Provider - ' + row.prvName + ' (' + row.prvPplId + ')'
      : 'Employer - ' + row.empName + ' (' + row.empPplId + ')';

    const dialogConfig: DialogConfig = {
      title: this.modalBody.associationModal,
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };
    this.dialogSvc.openDialog(this.associationDialogRef, dialogConfig, () => this.handleCancelForAssociation(), () => this.handleCancelForAssociation());

    this.loadLinkedChecklist();
  }

  // (Re)fetch the linked (Employer ⇄ Provider) checklist detail for the currently selected row and
  // rebuild the in-dialog forms tree. Called on open and again after a sign (forceFresh → primary DB)
  // so the dialog reflects the just-signed form status.
  private loadLinkedChecklist(forceFresh = false) {
    if (!this.selectedLinkedMode || !this.selectedLinkedRow) return;
    const isEmp = this.selectedLinkedMode === 'employerProvider';
    const row = this.selectedLinkedRow;

    const request$ = isEmp
      ? this.checkListService.getEmployerProviderChecklist(this.program?.program_code, this.userId || '', row.prvId, 'EMP', forceFresh)
      : this.checkListService.getProviderEmployerChecklist(this.program?.program_code, this.userId || '', row.empId, 'IR', forceFresh);

    request$.subscribe((data: any) => {
      if (data?.responsedata) {
        const checklist: ChecklistResponse = data.responsedata;
        this.associatedPPLId = checklist.pplId;
        // isForms = true → build the forms tree the wizard renders.
        this.providerAssociationChecklist = this.checklistHelperService.buildChecklistDataForProvider(checklist.checkList, row, true, false);
      }
    });
  }

  // prtcpntPrvdrIdAndTypes the forms wizard should send for the linked (Employer ⇄ Provider) forms.
  get linkedFormEntities(): { userType: string; primaryId: string }[] | undefined {
    if (!this.selectedLinkedMode || !this.selectedLinkedRow) return undefined;
    return [
      { userType: 'IR', primaryId: this.selectedLinkedRow.prvPplId },
      { userType: 'EMP', primaryId: this.selectedLinkedRow.empPplId },
    ];
  }

  // Save config for the linked forms wizard/renderer (routes save/sign to the new endpoints).
  get linkedFormSaveConfig(): { mode: 'employerProvider' | 'providerEmployer'; employerId: string; providerId: string; userType: string } | undefined {
    if (!this.selectedLinkedMode || !this.selectedLinkedRow) return undefined;
    const isEmp = this.selectedLinkedMode === 'employerProvider';
    return {
      mode: this.selectedLinkedMode,
      employerId: isEmp ? this.userId : String(this.selectedLinkedRow.empId),
      providerId: isEmp ? String(this.selectedLinkedRow.prvId) : this.userId,
      userType: isEmp ? 'EMP' : 'IR',
    };
  }

  // Read-only rule for the association forms wizard:
  //  - Employer viewing a provider's forms ('employerProvider')  → read-only (can sign, cannot edit fields).
  //  - Provider viewing an employer's forms ('providerEmployer')  → editable + signable.
  //  - Normal participant association → the tab's own readonly.
  get associationFormsReadonly(): boolean {
    if (this.selectedLinkedMode === 'employerProvider') return true;
    if (this.selectedLinkedMode === 'providerEmployer') return false;
    return this.readonly ?? false;
  }

  // PDF "sign only" rule (editpdf disables fields but keeps signing when SignOnly=true):
  //  - Employer viewing a provider's form → sign only, no field edits.
  //  - Provider viewing an employer's form → full field editing + sign.
  //  - Normal flow → the existing I9 / canAllowFormEditing rule.
  get pdfSignOnly(): boolean {
    if (this.selectedLinkedMode === 'employerProvider') return true;
    if (this.selectedLinkedMode === 'providerEmployer') return false;
    return !(this.isI9Form || this.canAllowFormEditing);
  }

  // PDF read-only for the editpdf dialog. For linked (Employer ⇄ Provider) forms it must stay false
  // so both roles can sign (editpdf blocks signing when readonly=true); the employer's no-edit is
  // enforced by pdfSignOnly instead. Normal flows keep the tab's own readonly.
  get pdfReadonly(): boolean {
    if (this.selectedLinkedMode) return false;
    return this.readonly ?? false;
  }

  private filterHiddenForms(items: ChecklistItem[]): ChecklistItem[] {
    // Provider Classification is captured by its own questionnaire (ppl-provider-classification),
    // so it must never surface as a form in the checklist.
    const visible = items.filter(item => item.formMasterId !== IC_PROVIDER_CLASSIFICATION_FORM.formMasterId && item.formMasterId !== VE_PROVIDER_CLASSIFICATION_FORM.formMasterId);

    if (this.isParticipantFlow || this.shouldShowHcbsEnclosureB()) {
      return visible;
    }
    return visible.filter(item => !this.HCBS_ENCLOSURE_B_FORM_IDS.includes(item.formMasterId));
  }

  private shouldShowHcbsEnclosureB(): boolean {
    if (!this.providerEnrollmentFlags) return true;
    const { servicesAtBusinessLocation, servingIndividualsWithDisabilities } = this.providerEnrollmentFlags;
    return this.isYes(servicesAtBusinessLocation) && this.isYes(servingIndividualsWithDisabilities);
  }

  private isYes(value: string | null | undefined): boolean {
    return typeof value === 'string' && value.trim().toLowerCase() === 'yes';
  }

  isAssociationsCompleted() {
    let isCompleted = true;
    for (const checklist of this.prvdrAssociations) {
      if (checklist.formStatus != 'C') {
        isCompleted = false;
        break;
      }
    }

    return isCompleted;
  }

  action(event: GridAction, isAssociation: boolean) {
    if (event.action == 'edit') {
      this.disableSave = true;
      if (isAssociation) {
        this.isSelf = false;
        this.handleAssociationCheckListClick(event.data)
      } else {
        this.isSelf = true;
        this.handleCheckListClick(event.data)
      }
    }
    else if (event.action == 'download') {
      this.handleDownload([event.data], isAssociation, isAssociation ? this.selectedParticipantPplId : this.pplId);
    }
    else if (event.action == 'view') {
      if (this.isHistory) {
        this.isSelf = !this.isHistoryAssociation;
        if (this.isHistoryAssociation) {
          this.handleAssociationCheckListClick(event.data, 'history');
        } else {
          this.handleCheckListClick(event.data, 'history');
        }
      }
      else {
        this.disableSave = true;
        this.selectedParticipant = event.data;
        this.selectedProviderId = event.data.providerId || this.userId;
        this.relationToParticipant = event.data.relationToParticipant;
        this.handleParticipantAssociationSelection(event.data);
      }
    } else if (event.action === 'requestMail') {
      this.selectedItemToMail.set(event.data);
      this.handleRequestMail();
    } else if (event.action === 'refetch') {
      // FLSA / Tax Exemptions save changes add or remove dependent forms. Force a primary-DB
      // read (isWriteRequest=true) so the new dependent form set is reflected — the read
      // replica may not have caught up yet from the save we just made.
      if (isAssociation && this.selectedParticipant) {
        // Refetch the association checklist that the dialog is currently showing.
        this.handleParticipantAssociationSelection(this.selectedParticipant, true, true);
      } else {
        this.loadData(true);
      }
    } else if (event.action === 'history') {
      const associationDetail = event.data?.details?.find((d: any) => d.associationId > 0);
      const isParticipantAssociation = this.isParticipantFlow && !!associationDetail;

      this.actions = {
        "view": true,
        "showChanges": true
      }
      this.isHistory = true;
      this.isHistoryAssociation = isAssociation || isParticipantAssociation;
      this.disableSave = true;
      this.readonlyBeforeHistory = this.readonly ?? false;
      this.providerTypeBeforeHistory = this.selectedProviderType;
      this.readonly = true;

      if (isParticipantAssociation) {
        this.selectedParticipantPplId = associationDetail.pplId;
        this.selectedProviderType = associationDetail.type;
        this.selectedProviderId = this.providerIdByPplId[associationDetail.pplId] || '';
      }

      this.handleHistory(event.data)
    }
    else if (event.action === 'showChanges') {
      this.handleShowChangesClick(event.data, 'history', isAssociation);
    }
  }

  handleShowChangesClick(data: any, from = '', isAssociation = false) {
    this.selectedForm = data;
    const isParticipantAssociation = this.isParticipantFlow && isAssociation;
    if (!isParticipantAssociation) {
      const currentuser = this.selectedForm.details.filter((d: any) => d.associationId == -1);
      this.selectedProviderType = currentuser.length > 0 ? currentuser[0].type : '';
    }
    this.showModal = true;
    this.modalBody.pdfViewer = 'Changes - ' + data.formDesc;
    const dialogConfig: DialogConfig = {
      title: this.modalBody.pdfViewer,
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    }
    this.dialogSvc.openDialog(this.showChangesDialogRef, dialogConfig, () => this.handleCancel(), () => this.handleCancel());
    const diffChangesEntry = data?.data.find((entry: { type: string; }) => entry.type === "DiffChanges");
    this.diffChanges = diffChangesEntry ? diffChangesEntry.value : null;
    this.highlightedFields = this.parseDiffChanges(this.diffChanges);
    if (data?.formDesc === 'Attestation of Health Assessment') {
      this.highlightedFields = this.highlightedFields?.filter(
        (field) => field?.path !== 'Personal Assistant Name Print'
      );
    }
    this.loadPDF(data, from, isAssociation);
  }

  async setPreviousValues(form: ChecklistItem, from = '', isAssociation = false) {
    const checklistItems = this.formHistoryChecklist?.[0]?.items || [];
    const currentIndex = checklistItems.findIndex(i => i.checklistId === form.checklistId);
    if (currentIndex === -1 || !checklistItems[currentIndex + 1]) {
      return;
    }
    const previousForm = checklistItems[currentIndex + 1];

    try {
      this.previousPdfConfig = await this.loadPDFDataForPrevious(previousForm, from, isAssociation);
      if (this.previousPdfConfig) {
        for (const patch of this.highlightedFields) {
          const prev = this.previousPdfConfig.fields.find(field => field.businessName === patch.path);
          if (prev?.fieldId) {
            patch["value"] = prev.value || '-';
          } else {
            for (const field of this.previousPdfConfig.fields) {
              const childMatch = field.childFields?.find(child => child.fieldId === patch.path);
              if (childMatch?.fieldId) {
                patch["value"] = childMatch.value;
                break;
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to load previous form config:', error);
    }
  }

  loadPDFDataForPrevious(previousForm: ChecklistItem, from = '', isAssociation = false): Promise<PDFFormConfig> {
    const pplId = isAssociation ? this.selectedParticipantPplId : this.pplId;
    return new Promise((resolve, reject) => {
      // Employer ⇄ Provider forms: send [{IR, prvPplId},{EMP, empPplId}] as prtcpntPrvdrIdAndTypes
      // (same override as loadPDFData) so the show-changes previous version loads the right data.
      this.checkListService.getFormData(this.program?.program_code, previousForm, this.source, pplId ?? '', from, isAssociation, this.linkedFormEntities)
        .subscribe({
          next: (response) => {
            if (response?.responsedata) {
              const config: PDFFormConfig = {
                fields: response.responsedata.Form_Fields,
                form_description: previousForm.formDesc,
                form_id: previousForm.formMasterId + '',
                form_name: previousForm.form_name,
                is_mandatory: false
              };
              resolve(config);
            } else {
              reject('No data found');
            }
          },
          error: (err) => reject(err)
        });
    });
  }

  parseDiffChanges(raw?: string): PDFDiffChanges[] {
    const regex = /op:\s*(\w+);\s*path:\s*"?\/?([^"]+)";\s*value:\s*"([^"]+)"/g;
    let showDiff: PDFDiffChanges[] = [];
    if (raw) {
      showDiff = Array.from(raw.matchAll(regex)).map(match => ({
        op: match[1],
        path: this.decodeJsonPointerPath(match[2]),
        value: match[3]
      }));
    }

    return showDiff;
  }

  decodeJsonPointerPath(path: string) {
    return path.replace(/~1/g, '/').replace(/~0/g, '~');
  }

  handleHistory(data: any, fromClosedDialog?: boolean) {
    this.showAssociationModal = true;
    this.modalBody.associationModal = 'History Of Forms - ' + data.formDesc;
    const associationDetail = data.details?.find((d: any) => d.associationId > 0);
    const isAssociation = this.isHistoryAssociation || (this.isParticipantFlow && !!associationDetail);

    if (this.selectedParticipant) {
      this.selectedParticipantId = this.selectedParticipant.participantId || (this.isParticipantFlow ? +this.userId : undefined);
      this.selectedProviderType = this.selectedParticipant.providerType;
    } else if (isAssociation) {
      this.selectedParticipantId = this.isParticipantFlow ? +this.userId : undefined;
      this.selectedProviderType = associationDetail?.type || '';
    }
    const dialogConfig: DialogConfig = {
      title: this.modalBody.associationModal,
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    }

    const statusMap: Record<string, string> = {
      C: 'Completed',
      R: 'Form Returned'
    };

    const entityId = isAssociation ? (this.selectedParticipant?.providerId || this.selectedProviderId || this.providerIdByPplId[associationDetail?.pplId ?? ''] || this.userId) : this.userId;
    const isParticipantAssociationHistory = this.isParticipantFlow && isAssociation && !this.selectedParticipant && !!associationDetail;
    const participantAssociationContext = isParticipantAssociationHistory ? {
      providerPPLID: associationDetail.pplId,
      providerType: associationDetail.type,
      providerTypeLabel: associationDetail.typeDesc,
      participantProviderAssociationId: associationDetail.associationId,
      relationship: associationDetail.relationship
    } : null;
    const history$ = this.selectedLinkedMode
      ? this.checkListService.getLinkedFormHistory(this.program?.program_code, this.selectedLinkedMode, this.linkedFormSaveConfig!.providerId, this.linkedFormSaveConfig!.employerId, data.checklistItemId)
      : this.checkListService.getCheckListHistory(this.program?.program_code, this.selectedParticipantId ? `${this.selectedParticipantId}` : '', entityId, this.selectedProviderType ? this.selectedProviderType : '', data.checklistItemId, this.source, isAssociation);
    history$
      .subscribe((data) => {
        if (data.responsedata) {
          const checklist: ChecklistResponse = data.responsedata;
          if (this.selectedLinkedMode) {
            this.formHistoryChecklist = this.checklistHelperService
              .buildHistoryDataForProvider(checklist.checkList, this.selectedLinkedRow, true, false)
              .map(group => ({
                ...group,
                items: [...group.items]
                  .map(item => ({
                    ...item,
                    createdDate: this.formatDateToAMPM(item.createdDate),
                    modifiedDate: this.formatDateToAMPM(item.modifiedDate),
                    status: statusMap[item.formStatus] || 'Not Completed'
                  }))
                  .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
              }));
          } else if (this.selectedParticipant) {
            this.associatedPPLId = checklist.pplId;
            this.selectedParticipant.providerPPLID = checklist.pplId;
            this.formHistoryChecklist = this.checklistHelperService
              .buildHistoryDataForProvider(checklist.checkList, this.selectedParticipant, true, false)
              .map(group => ({
                ...group,
                items: [...group.items]
                  .map(item => ({
                    ...item,
                    createdDate: this.formatDateToAMPM(item.createdDate),
                    modifiedDate: this.formatDateToAMPM(item.modifiedDate),
                    status: statusMap[item.formStatus] || 'Not Completed'
                  }))
                  .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
              }));
          } else if (participantAssociationContext) {
            this.associatedPPLId = checklist.pplId;
            this.formHistoryChecklist = this.checklistHelperService
              .buildHistoryDataForProvider(checklist.checkList, participantAssociationContext, true, false)
              .map(group => ({
                ...group,
                items: [...group.items]
                  .map(item => ({
                    ...item,
                    createdDate: this.formatDateToAMPM(item.createdDate),
                    modifiedDate: this.formatDateToAMPM(item.modifiedDate),
                    status: statusMap[item.formStatus] || 'Not Completed'
                  }))
                  .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
              }));
          } else {
            this.formHistoryChecklist = this.checklistHelperService
              .buildHistoryDataForProvider(checklist.checkList, data, true, true)
              .map(group => ({
                ...group,
                items: [...group.items]
                  .map(item => ({
                    ...item,
                    createdDate: this.formatDateToAMPM(item.createdDate),
                    modifiedDate: this.formatDateToAMPM(item.modifiedDate),
                    status: statusMap[item.formStatus] || 'Not Completed'
                  }))
                  .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
              }));
          }
        }
      });


    if (!fromClosedDialog) {
      this.dialogSvc.openDialog(this.formHistoryDialogRef, dialogConfig, () => this.handleCancelForHistory(), () => this.handleCancelForHistory());
    }
  }

  formatDateToAMPM(dateStr: string): string {
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleString('en-US', options);
  }

  handleCancelForHistory(): void {
    this.isHistory = false;
    this.isHistoryAssociation = false;
    this.readonly = this.readonlyBeforeHistory;
    this.selectedProviderType = this.providerTypeBeforeHistory;
    if (this.isParticipantFlow) {
      this.selectedParticipantPplId = undefined;
      this.selectedProviderId = '';
      this.selectedParticipantId = undefined;
    }
    this.actions = {
      "edit": true,
      "download": true,
      "mail": !this.readonly,
      "history": true,
      "return": true
    }
    this.loadData();
  }

  handleRequestMail() {
    const dialogConfig: DialogConfig = {
      title: 'Confirm Mail',
      okText: 'Mail Form',
      size: 'medium',
      position: 'top',
      showButtons: true,
    }
    this.dialogSvc.openDialog(
      this.confirmationDialog2,
      dialogConfig,
      () => this.handleRequestMailClick(this.selectedItemToMail()),
      () => this.onCloseDialog()
    );
  }

  handleParticipantAssociationSelection(selectedParticipant: any, fromClosedDialog?: boolean, forceFresh = false) {
    this.showAssociationModal = true;
    // Normal participant association → clear any Employer⇄Provider linked mode so getFormData
    // uses the standard participant path, not the [{IR},{EMP}] override.
    this.selectedLinkedMode = null;
    this.selectedLinkedRow = undefined;
    this.modalBody.associationModal = 'Participant Association - ' + selectedParticipant.participantFirstName + ' ' + selectedParticipant.participantLastName + ' (' + selectedParticipant.participantPplId + ')';
    this.selectedParticipantId = selectedParticipant.participantId;
    this.selectedProviderType = selectedParticipant.providerType;
    this.selectedParticipantPplId = selectedParticipant.participantPplId;
    const dialogConfig: DialogConfig = {
      title: this.modalBody.associationModal,
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    }

    if (!fromClosedDialog) {
      this.dialogSvc.openDialog(this.associationDialogRef, dialogConfig, () => this.handleCancelForAssociation(), () => this.handleCancelForAssociation());
    }

    this.checkListService.getChecklistForParticipantAssociation(this.program?.program_code, selectedParticipant.providerId, selectedParticipant.participantId, selectedParticipant.providerType, forceFresh).subscribe((data: any) => {
      if (data.responsedata) {
        const checklist: ChecklistResponse = data.responsedata;
        checklist?.checkList.forEach((item: ChecklistItem) => {
          this.applyPendingResignState(item);
        });
        checklist?.checkList.forEach((checklist: any) => {
          checklist['arialabel'] = checklist.formDesc + 'Status ' + (checklist.formStatus == 'C' ? 'Completed' : 'Not Completed');
        });
        this.associatedPPLId = checklist.pplId;
        selectedParticipant.providerPPLID = checklist.pplId;
        this.providerAssociationChecklist = this.checklistHelperService.buildChecklistDataForProvider(checklist.checkList, selectedParticipant, true, false);
      }
    });
  }

  handleDownload(forms: ChecklistItem[], isAssociation = false, participantId: any) {
    const pdfPayload: DownloadFormPayloadItem[] = [];
    let customFormName = '';
    forms.forEach(f => {
      if (f.formMasterId != 0) {
        let userDetails = f.details.map(e => Object({ userType: e.type, primaryId: e.pplId }));
        if (this.selectedLinkedMode && this.linkedFormEntities) {
          // Employer ⇄ Provider forms: download with the explicit [{IR, prvPplId},{EMP, empPplId}] entities.
          userDetails = this.linkedFormEntities.map(e => Object({ userType: e.userType, primaryId: e.primaryId }));
        }
        else if (this.source == 'participant' && userDetails.filter((e: any) => e.userType == 'PTC').length == 0) {
          userDetails.push(new Object({ userType: 'PTC', primaryId: participantId }));
        }
        else if (isAssociation && this.source == 'provider') {
          if (userDetails && !userDetails.some((e: any) => e.userType == "PTC") && userDetails.some((e: any) => e.userType == "EMP")) {
            const empEntry = userDetails.find((d: any) => d.userType === 'EMP');
            userDetails = userDetails.filter((d: any) => d.userType !== 'EMP');
            userDetails.push({
              primaryId: participantId,
              userType: "PTC",
            });
            if (empEntry) {
              userDetails.push({
                primaryId: empEntry.primaryId,
                userType: "EMP",
              });
            }
          }
        }


        if(userDetails && userDetails.length == 1) {
          customFormName = PROVIDER_TYPES[userDetails[0].userType];
        }

        const payload = {
          formId: f.formMasterId + '',
          checkListItemId: f.checklistItemId,
          checkListItemDtlsId: f.checklistItemDetailId,
          source: this.source,
          programCode: this.program?.program_code,
          languageCode: 'ENG',
          prtcpntPrvdrIdAndTypes: userDetails
        };

        pdfPayload.push(payload);
      }
    });

    const formPayload: DownloadFormPayload = {
      forms: [...pdfPayload]
    };

    this.checkListService.downloadPDFForm(formPayload, this.generateFileName(isAssociation, customFormName));
  }

  private generateFileName(isAssociation: boolean, customFormName: string): string {
    const timestamp = this.dateTimeFormatterService.formatDateTimeForFilename(new Date());

    if (environment.isExternal) {
      const role = this.userService.getSelectedRole()?.role_name || 'user';
      return `${role.replaceAll(' ', '_').toLowerCase()}_forms_${timestamp}.zip`;
    }

    if (this.source === 'participant') {
      return `participant_forms_${timestamp}.zip`;
    }

    if(customFormName) {
      return `${customFormName.toLowerCase()}_forms_${timestamp}.zip`;
    }

    const fileName = this.selectedProviderType ? PROVIDER_TYPES[this.selectedProviderType].toLowerCase() : this.source;
    return fileName + '_forms_' + timestamp + '.zip';
  }

  loadQRCode(f: ChecklistItem, from = '', isAssociation = false) {
    if (f.formMasterId != 0) {
      let userDetails = f.details.map(e => Object({ userType: e.type, primaryId: e.pplId }));
      if (this.source == 'participant' && userDetails.filter((e: any) => e.userType == 'PTC').length == 0) {
        userDetails.push(new Object({ userType: 'PTC', primaryId: this.pplId }));
      } else if (isAssociation && this.source == 'provider') {
        if (userDetails && !userDetails.some((e: any) => e.userType == "PTC") && userDetails.some((e: any) => e.userType == "EMP")) {
          const empData = userDetails.find((d: any) => d.userType == 'EMP');
          userDetails = userDetails.filter((d: any) => d.userType !== 'EMP');
          userDetails.push({
            primaryId: isAssociation ? this.selectedParticipantPplId : this.pplId,
            userType: "PTC",
          });
        }
      }
      const payload = {
        formId: f.formMasterId + '',
        checkListItemId: f.checklistItemId,
        checkListItemDtlsId: f.checklistItemDetailId,
        source: this.source,
        programCode: this.program?.program_code,
        languageCode: 'ENG',
        prtcpntPrvdrIdAndTypes: userDetails
      };

      this.checkListService.getFormQRCode(payload).subscribe((response: HttpResponse<Blob>) => {
        if (response.status === 200) {
          this.qrCode = response.body;
        }
      });
    }
  }

  handleCheckListClick(form: ChecklistItem, from = '') {
    this.selectedForm = form;
    const currentuser = this.selectedForm.details.filter((d: any) => d.associationId == -1);
    this.selectedProviderType = currentuser.length > 0 ? currentuser[0].type : '';
    this.showModal = true;
    this.modalBody.pdfViewer = form.formDesc;
    const dialogConfig: DialogConfig = {
      title: this.modalBody.pdfViewer,
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    }
    this.dialogSvc.openDialog(this.pdfDialogRef, dialogConfig, () => this.handleCancel(), () => this.handleCancel());

    this.loadPDF(form, from);
  }

  handleAssociationCheckListClick(form: ChecklistItem, from = '') {
    this.selectedAssociationForm = form;
    if (this.isParticipantFlow || !this.selectedProviderType) {
      const selfDetail = form.details.find((d: any) => d.associationId == -1);
      const assocDetail = form.details.find((d: any) => d.associationId > 0);
      this.selectedProviderType = selfDetail?.type || assocDetail?.type || '';
    }
    this.showModal = true;
    this.modalBody.pdfViewer = form.formDesc;
    const dialogConfig: DialogConfig = {
      title: this.modalBody.pdfViewer,
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    }
    this.dialogSvc.openDialog(this.pdfDialogRef, dialogConfig, () => this.handleCancel(), () => this.handleCancel());
    this.loadPDF(form, from, true);
  }

  handleCancel() {
    const currentPdfFile = this.pdfFile;
    const shouldRefreshAfterClose = this.hasPersistedPdfChanges;
    this.showModal = false;
    this.pdfConfig = undefined;
    this.pdfFile = undefined;
    this.pdfBlob = undefined;
    this.selectedForm = undefined;
    this.signForm = new Subject();
    this.disableSign = false;
    this.relationToParticipant = '';
    this.highlightedFields = [];
    this.diffChanges = undefined;
    this.previousPdfConfig = undefined;
    this.hasPersistedPdfChanges = false;
    this.schedulePdfBlobCleanup(currentPdfFile);
    if (!shouldRefreshAfterClose) {
      return;
    }

    if (this.selectedParticipant) {
      this.handleParticipantAssociationSelection(this.selectedParticipant, true);
    } else {
      this.loadData();
    }
  }

  handleCancelForAssociation() {
    const currentPdfFile = this.pdfFile;
    const shouldRefreshAfterClose = this.hasPersistedPdfChanges;
    this.showModal = false;
    this.pdfConfig = undefined;
    this.pdfFile = undefined;
    this.pdfBlob = undefined;
    this.selectedForm = undefined;
    this.selectedAssociationForm = undefined;
    this.showAssociationModal = false;
    this.providerAssociationChecklist = undefined;
    this.selectedParticipantId = undefined;
    this.selectedParticipantPplId = undefined;
    this.selectedParticipant = undefined;
    this.associatedPPLId = undefined;
    this.signForm = new Subject();
    this.disableSign = false;
    this.relationToParticipant = '';
    this.hasPersistedPdfChanges = false;
    this.selectedLinkedMode = null;
    this.selectedLinkedRow = undefined;
    this.schedulePdfBlobCleanup(currentPdfFile);
    if (shouldRefreshAfterClose) {
      this.loadData(true);
    }
  }

  fieldsUpdated(event: any) {
    this.formData = event.data;
    this.errors = event.errors;
    if (this.hasNoRates) {
      this.disableSave = true;
      this.customError = 'Rates are not available.';
    } else {
      this.disableSave = this.errors.length != 0 || event.customError != '';
      this.customError = event.customError;
    }
    this.enforceI9CitizenshipConflict(event.data);
  }

  /**
   * Scenario 5: if the provider sets the I-9 citizenship to U.S. citizen while the Tax Exemptions
   * form still has the non-resident-alien option selected, block saving the I-9 and show a message
   * telling them to update the Tax Exemptions form first. The I-9 stays editable; only the save of
   * this conflicting change is blocked.
   */
  private enforceI9CitizenshipConflict(data: any): void {
    if (!this.isI9Form || !data) {
      return;
    }
    const citizenship = data[this.I9_CITIZENSHIP_FIELD];
    if (citizenship === this.I9_US_CITIZEN_VALUE && this.taxHasNonResidentAlien) {
      this.disableSave = true;
      this.customError = 'The Tax Exemptions form has the non-resident alien option selected. Please update the Tax Exemptions form before setting your I-9 citizenship to U.S. citizen.';
    }
  }

  /** Fetch the Tax Exemptions form's saved non-resident-alien selection for the Scenario 5 check. */
  private prefetchTaxExemptionSelection(isAssociation = false): void {
    this.taxHasNonResidentAlien = false;
    const taxItem = this.findTaxExemptionsItem();
    if (!taxItem) {
      return;
    }
    const pplId = isAssociation ? this.selectedParticipantPplId : this.pplId;
    this.checkListService
      .getFormData(this.program?.program_code, taxItem, this.source, pplId ?? '', '', isAssociation)
      .subscribe((response: any) => {
        this.taxHasNonResidentAlien = this.isNonResidentAlienSelected(response?.responsedata?.Form_Fields);
      });
  }

  private findTaxExemptionsItem(): any {
    for (const group of this.providerAssociationChecklist || []) {
      const item = group.items.find((i: any) => i.formMasterId === this.TAX_EXEMPTIONS_FORM_ID);
      if (item) {
        return item;
      }
    }
    return undefined;
  }

  private isNonResidentAlienSelected(fields: any[]): boolean {
    if (!Array.isArray(fields)) {
      return false;
    }
    const match = fields.find((f: any) =>
      String(f?.fieldId ?? '').trim() === this.NON_RESIDENT_ALIEN_KEY ||
      String(f?.businessName ?? '').trim() === this.NON_RESIDENT_ALIEN_KEY);
    if (!match) {
      return false;
    }
    const value = match.value;
    return value === 'Yes' || value === true || value === this.NON_RESIDENT_ALIEN_KEY;
  }

  updateMandatoryFormsCompleteStatus() {
    let isCompleted = true;
    for (const checklist of this.checkListData) {
      if (checklist.items.filter(e => e.mandatory && e.formStatus != 'C').length > 0) {
        isCompleted = false;
        break;
      }
    }

    for (const checklist of this.prvdrAssociations) {
      if (checklist.checklistStatus == 'P') {
        isCompleted = false;
        break;
      }
    }

    this.isMandatoryFormsCompleted.emit(isCompleted);
  }

  handleSave() {
    if (this.selectedLinkedMode) {
      this.handleSaveLinkedForm();
      return;
    }
    if (this.selectedParticipantId) {
      this.handleSaveAssociation();
      return;
    }

    if (this.errors.length == 0) {
      const checklistSaveResponse = this.generateSaveChecklistResponses();
      const payload = {
        type: this.source,
        checklistItems: checklistSaveResponse.filter(e => e != null)
      };
      this.checkListService.saveParticipantProviderForms(this.program?.program_code, this.userId, this.isParticipantFlow, payload).subscribe((response: HttpResponse<Blob>) => {
        if (response.status === 200) {
          this.setPendingResignState(this.selectedForm, false);
          this.hasPersistedPdfChanges = true;
          this.loadPDFData(this.selectedForm);
          this.disableSave = true;
          this.isPdfSubmitted = true;

          this.viewEditPDFComponent.onFormSubmission();
        }
      });
    }
  }

  handleSaveAssociation() {
    if (this.errors.length == 0 && this.providerAssociationChecklist && this.selectedParticipantId) {
      const checklistSaveResponse = this.generateSaveChecklistResponsesForAssociation(this.selectedParticipantId);
      const payload = {
        type: "participantProvider",
        action: "sign",
        checklistItems: checklistSaveResponse.filter(e => e != null)
      };
      this.checkListService.saveParticipantAssocaitionForms(this.program?.program_code,
        this.selectedParticipantId, this.userId, this.selectedProviderType || '', payload).subscribe((response: HttpResponse<Blob>) => {
          if (response.status === 200) {
            this.setPendingResignState(this.selectedAssociationForm, false);
            this.hasPersistedPdfChanges = true;
            this.loadPDFData(this.selectedAssociationForm);
            this.disableSave = true;
            this.isPdfSubmitted = true;

            this.viewEditPDFComponent.onFormSubmission();
          }
        });
    }
  }

  // Save (sign) a linked Employer ⇄ Provider form to the new form-save endpoints.
  handleSaveLinkedForm() {
    if (this.errors.length !== 0 || !this.selectedAssociationForm || !this.selectedLinkedMode || !this.selectedLinkedRow) {
      return;
    }
    const cfg = this.linkedFormSaveConfig!;
    const form = this.selectedAssociationForm;
    const payload = {
      type: cfg.mode,
      // This path is the signed-PDF submission → always 'sign'.
      action: 'sign',
      checklistItems: [{
        providerType: 'IR',
        providerId: Number(cfg.providerId),
        formValue: stripLegacyTableCellKeys(this.formData),
        checklistDetailId: form.checklistItemDetailId,
        checklistId: form.checklistItemId,
      }],
    };

    const save$ = cfg.mode === 'employerProvider'
      ? this.checkListService.saveEmployerProviderForm(this.program?.program_code, cfg.employerId, cfg.providerId, cfg.userType, payload)
      : this.checkListService.saveProviderEmployerForm(this.program?.program_code, cfg.employerId, cfg.providerId, cfg.userType, payload);

    save$.subscribe((response: HttpResponse<Blob>) => {
      if (response.status === 200) {
        this.setPendingResignState(this.selectedAssociationForm, false);
        this.hasPersistedPdfChanges = true;
        this.loadPDFData(this.selectedAssociationForm);
        // Re-fetch the linked checklist detail (primary DB) so the in-dialog form list reflects the
        // just-signed status without needing to close/reopen the dialog.
        this.loadLinkedChecklist(true);
        this.disableSave = true;
        this.isPdfSubmitted = true;
        this.viewEditPDFComponent.onFormSubmission();
      }
    });
  }

  generateSaveChecklistResponses() {
    const checklist: any[] = [];
    if (this.selectedForm) {
      const currentuser = this.selectedForm.details.filter((d: any) => d.associationId == -1);
      checklist.push(new Object({
        checklistId: this.selectedForm.checklistItemId,
        checklistDetailId: this.selectedForm.checklistItemDetailId,
        participantId: this.isParticipantFlow ? this.userId : '',
        providerId: !this.isParticipantFlow ? this.userId : '',
        providerType: currentuser.length > 0 ? currentuser[0].type : '',
        associationId: this.selectedForm.details.filter((e: any) => e.associationId != -1).map((d: any) => d.associationId),
        formValue: stripLegacyTableCellKeys(this.formData),
      }));
    }

    return checklist;
  }

  generateSaveChecklistResponsesForAssociation(participantId: number) {
    const checklist: any[] = [];
    if (this.selectedAssociationForm) {
      checklist.push(new Object({
        checklistId: this.selectedAssociationForm.checklistItemId,
        checklistDetailId: this.selectedAssociationForm.checklistItemDetailId,
        participantId: participantId,
        providerId: this.userId,
        providerType: this.selectedProviderType,
        associationId: this.selectedAssociationForm.details.filter((d: any) => d.associationId != -1).map((d: any) => d.associationId),
        formValue: stripLegacyTableCellKeys(this.formData),
      }));
    }
    return checklist;
  }

  private getPendingResignStorageKey(form: ChecklistItem | undefined): string {
    if (!form) {
      return '';
    }

    return `provider-form-pending-resign:${form.checklistItemId}:${form.checklistItemDetailId}`;
  }

  private setPendingResignState(form: ChecklistItem | undefined, isPending: boolean): void {
    const storageKey = this.getPendingResignStorageKey(form);
    if (!storageKey) {
      return;
    }

    if (isPending) {
      sessionStorage.setItem(storageKey, 'true');
      return;
    }

    sessionStorage.removeItem(storageKey);
  }

  private applyPendingResignState(form: ChecklistItem | undefined): void {
    const storageKey = this.getPendingResignStorageKey(form);
    if (!storageKey || !form) {
      return;
    }

    if (sessionStorage.getItem(storageKey) === 'true') {
      form.formStatus = 'I';
    }
  }

  loadPDF(form: ChecklistItem, from = '', isAssociation = false) {
    this.errors = [];
    this.customError = '';
    this.hasNoRates = false;
    this.formData = undefined;
    this.hasPersistedPdfChanges = false;
    this.applyPendingResignState(form);
    this.isPdfSubmitted = form.formStatus == 'C';
    this.isI9Form = form.formMasterId === 109 || form.formDesc.trim().toLowerCase() == 'USCIS Form I-9 Form'.toLowerCase();
    // When opening the I-9, prefetch the Tax Exemptions non-resident-alien selection so we can block
    // (Scenario 5) setting citizenship to U.S. citizen while it is still selected.
    if (this.isI9Form) {
      this.prefetchTaxExemptionSelection(isAssociation);
    }
    // Currently we are allowing editing for I9 form, W9 form and HCSBS forms. This is based on the current requirements but can be made configurable in the future if needed.
    // HCBS forms (110/122/128) are filled by the provider; the participant only signs after the provider has signed.
    const isHcbsForm = [110, 122, 128].includes(form.formMasterId);
    const allowHcbsEditing = isHcbsForm && !this.isParticipantFlow;
    this.canAllowFormEditing = this.isI9Form || [107, 123, 124].includes(form.formMasterId) || allowHcbsEditing;
    this.resignRequired = true;
    this.checkListService.getPDF(this.program?.program_code, form.formMasterId, 'eng').subscribe((response: HttpResponse<Blob>) => {
      if (response.body) {
        this.schedulePdfBlobCleanup(this.pdfFile);
        const blob = new Blob([response.body], { type: response.body.type });
        this.pdfBlob = blob;
        this.pdfFile = window.URL.createObjectURL(blob);
      }
    });

    this.loadPDFData(form, from, isAssociation);
    this.loadQRCode(form, from, isAssociation);
  }

  loadPDFData(form: ChecklistItem, from = '', isAssociation = false) {
    const requestToken = ++this.pdfConfigRequestToken;
    const pplId = isAssociation ? this.selectedParticipantPplId : this.pplId;
    // For Employer ⇄ Provider forms, send both provider entities as prtcpntPrvdrIdAndTypes
    // ([{IR, prvPplId}, {EMP, empPplId}]) instead of the participant PTC remap.
    const linkedEntities = this.selectedLinkedMode && this.selectedLinkedRow
      ? [
          { userType: 'IR', primaryId: this.selectedLinkedRow.prvPplId },
          { userType: 'EMP', primaryId: this.selectedLinkedRow.empPplId },
        ]
      : undefined;
    this.checkListService.getFormData(this.program?.program_code,
      form,
      this.source, pplId ?? '', from, isAssociation, linkedEntities).subscribe(response => {
        if (!this.isLatestPdfConfigRequest(requestToken)) {
          return;
        }

        if (response?.responsedata) {
          const config: PDFFormConfig = {
            fields: response?.responsedata.Form_Fields,
            form_description: form.formDesc,
            form_id: form.formMasterId + '',
            form_name: form.form_name,
            is_mandatory: false
          };

          // For service code forms, fetch and merge service codes BEFORE setting pdfConfig
          // so EditpdfComponent initializes with the prepopulated values
          if (this.SERVICE_CODE_FORM_IDS.includes(form.formMasterId)) {
            this.loadServiceCodesForForm(form, config, requestToken);
          } else {
            this.setPdfConfigIfLatest(requestToken, config);
          }
          this.getHighlightedFields();
          this.setPreviousValues(form, from, isAssociation);
        }
      });
  }

  getHighlightedFields() {
    if (this.pdfConfig) {
      // Step 1: Filter out unwanted patches
      this.highlightedFields = this.highlightedFields.filter(patch => {
        // Try to find matching parent field
        const match = this.pdfConfig?.fields.find(field => field.businessName === patch.path);

        if (match) {
          if (
            patch.op === 'add' &&
            (match.fieldType === 'PDCheckBox' || match.fieldType === 'PDRadioButton') &&
            match.value === 'Off'
          ) {
            return false; // Remove patch
          }
          return true;
        } else {
          if (this.pdfConfig) {
            for (const field of this.pdfConfig.fields) {
              const childMatch = field.childFields?.find(child => child.fieldId === patch.path);
              if (
                patch.op === 'add' &&
                (field.fieldType === 'PDCheckBox' || field.fieldType === 'PDRadioButton') &&
                childMatch?.value === 'Off'
              ) {
                return false; // Remove patch
              }
            }
          }
          return true;
        }
      });


      // Step 2: Process remaining highlighted patches
      for (const patch of this.highlightedFields) {
        const match = this.pdfConfig.fields.find(field => field.businessName === patch.path);

        if (patch.op === 'add') {
          patch["value"] = "-";
        }

        if (match?.fieldId) {
          patch["fieldId"] = match.fieldId;
          patch["currentValue"] = match.value;
        } else {
          for (const field of this.pdfConfig.fields) {
            const childMatch = field.childFields?.find(child => child.fieldId === patch.path);
            if (childMatch?.fieldId) {
              patch["fieldId"] = childMatch.fieldId;
              patch["currentValue"] = childMatch.value;
              break;
            }
          }
        }
      }
    }
  }

  /** Fetches service codes from the API and merges them into the PDF form fields */
  private loadServiceCodesForForm(form: ChecklistItem, config: PDFFormConfig, requestToken: number) {
    const participantDetail = form.details?.find(d => d.type === 'PTC');
    const participantId = this.normalizeNumericId(this.selectedParticipantId ?? participantDetail?.pplId);
    const providerId = this.normalizeNumericId(this.selectedProviderId || this.userId);

    if (!participantId || !providerId) {
      console.warn('Skipping service code prepopulation due to missing/invalid numeric IDs', {
        formMasterId: form.formMasterId,
        participantId,
        providerId
      });
      this.setPdfConfigIfLatest(requestToken, config);
      return;
    }

    this.consumerRateService.getFormServiceCodes(this.program.program_code, participantId, providerId)
      .subscribe({
        next: (response: { responsedata?: unknown }) => {
          if (!this.isLatestPdfConfigRequest(requestToken)) {
            return;
          }

          const serviceCodes = this.parseServiceCodes(response?.responsedata);
          this.mergeServiceCodesIntoPdfFields(serviceCodes, config, form.formMasterId);
          this.hasNoRates = !this.formFieldsHaveRates(config);
          this.setPdfConfigIfLatest(requestToken, config);
        },
        error: (err: unknown) => {
          if (!this.isLatestPdfConfigRequest(requestToken)) {
            return;
          }

          console.error('Failed to load service codes for form', form.formMasterId, err);
          this.hasNoRates = !this.formFieldsHaveRates(config);
          this.setPdfConfigIfLatest(requestToken, config);
        }
      });
  }

  /**
   * Maps service code data into PDF form fields.
   * The PDF has two columns: "Service Name and Service Code" and "Hourly Wage"/"Hourly Rate".
   */
  private mergeServiceCodesIntoPdfFields(
    serviceCodes: Array<{ code?: string; serviceDescription?: string; rate?: number | null }>,
    config: PDFFormConfig,
    formMasterId: number
  ) {
    if (!config || !Array.isArray(serviceCodes) || serviceCodes.length === 0) {
      return;
    }

    const serviceNameValues = serviceCodes.map(serviceCode => this.formatServiceNameAndCode(serviceCode));
    const rateValues = serviceCodes.map(serviceCode => serviceCode.rate != null ? String(serviceCode.rate) : '');

    const serviceNameFields = this.findOrderedFieldsByLabel(config, this.SERVICE_NAME_FIELD_LABELS);
    const hourlyAmountFields = this.findOrderedFieldsByLabel(config, this.HOURLY_AMOUNT_FIELD_LABELS);

    if (serviceNameFields.length === 0 || hourlyAmountFields.length === 0) {
      console.warn('Service code mapping fields not found in PDF config', {
        formMasterId,
        serviceNameFields: serviceNameFields.length,
        hourlyAmountFields: hourlyAmountFields.length,
        totalFields: config.fields.length
      });
      return;
    }

    if (serviceCodes.length > serviceNameFields.length || serviceCodes.length > hourlyAmountFields.length) {
      console.warn('Service code mapping has fewer target rows than response rows', {
        formMasterId,
        responseRows: serviceCodes.length,
        serviceNameRows: serviceNameFields.length,
        hourlyAmountRows: hourlyAmountFields.length
      });
    }

    this.applyValuesToFields(serviceNameFields, serviceNameValues);
    this.applyValuesToFields(hourlyAmountFields, rateValues);
  }

  private setPdfConfigIfLatest(requestToken: number, config: PDFFormConfig) {
    if (this.isLatestPdfConfigRequest(requestToken)) {
      this.pdfConfig = config;
    }
  }

  private isLatestPdfConfigRequest(requestToken: number) {
    return requestToken === this.pdfConfigRequestToken;
  }

  private normalizeNumericId(value: unknown) {
    const normalized = String(value ?? '').trim();
    return /^\d+$/.test(normalized) ? normalized : '';
  }

  /** Formats service label as "Service Name : Service Code" */
  private formatServiceNameAndCode(serviceCode: { code?: string; serviceDescription?: string }) {
    const code = String(serviceCode?.code ?? '').trim();
    const rawDescription = String(serviceCode?.serviceDescription ?? '').trim();

    if (!rawDescription) {
      return code;
    }

    const parts = rawDescription.split(':');
    if (parts.length >= 2) {
      const left = parts[0].trim();
      const right = parts.slice(1).join(':').trim();

      if (code && left.toLowerCase() === code.toLowerCase()) {
        return `${right} : ${code}`;
      }

      if (code && right.toLowerCase() === code.toLowerCase()) {
        return `${left} : ${code}`;
      }

      return rawDescription;
    }

    if (code) {
      return `${rawDescription} : ${code}`;
    }

    return rawDescription;
  }

  private applyValuesToFields(fields: Array<{ value?: string }>, values: string[]) {
    values.forEach((value, index) => {
      if (!value) {
        return;
      }

      const field = fields[index];
      if (field) {
        field.value = value;
      }
    });
  }

  private parseServiceCodes(responsedata: unknown) {
    if (!Array.isArray(responsedata)) {
      return [] as Array<{ code?: string; serviceDescription?: string; rate?: number | null }>;
    }

    return responsedata as Array<{ code?: string; serviceDescription?: string; rate?: number | null }>;
  }

  /** Checks if the PDF form fields already have rate values populated (e.g. from a previous save) */
  private formFieldsHaveRates(config: PDFFormConfig): boolean {
    const rateFields = this.findOrderedFieldsByLabel(config, this.SERVICE_NAME_FIELD_LABELS);
    return rateFields.some(field => field.value != null && String(field.value).trim() !== '');
  }

  /** Finds PDF fields for a given column label and returns them in row order */
  private findOrderedFieldsByLabel(config: PDFFormConfig, labels: string[]) {
    const normalizedLabels = labels.map(label => this.normalizeFieldName(label));

    const matchingFields = config.fields.filter(field => {
      const businessName = this.normalizeFieldName(field.businessName ?? '');
      return normalizedLabels.some(label => businessName.includes(label));
    });

    return matchingFields.sort((a, b) => {
      const aRow = this.extractRowNumber(a.businessName ?? '');
      const bRow = this.extractRowNumber(b.businessName ?? '');

      if (aRow !== bRow) {
        return aRow - bRow;
      }

      return (a.businessName ?? '').localeCompare(b.businessName ?? '');
    });
  }

  private normalizeFieldName(value: string) {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private extractRowNumber(value: string) {
    const matches = value.match(/(\d+)/g);
    if (!matches || matches.length === 0) {
      return Number.MAX_SAFE_INTEGER;
    }
    return Number(matches[matches.length - 1]);
  }

  hideAll() {
    this.checkListData.forEach(group => {
      group.isActive = false;

      this.toggleCollapsePanel(group, false);
    });
  }

  expandAll() {
    this.checkListData.forEach(group => {
      group.isActive = true;

      this.toggleCollapsePanel(group, true);
    });
  }

  toggleCollapsePanel(group: ChecklistGroup, activate: boolean) {
    group.isActive = activate;
    if (group.children && group.children.length > 0) {
      group.children.forEach(c => {
        this.toggleCollapsePanel(c, activate);
      })
    }
  }

  isPanelActive(group: ChecklistGroup): boolean {
    return this.isMobile ? true : group.isActive;
  }

  onPanelHeaderKeyPress(group: ChecklistGroup) {
    group.isActive = !group.isActive;

    if (group.isActive) {
      setTimeout(() => {
        const panelEl = document.querySelector(
          `[data-checklist-id="${group.checklistName}"]`
        );
        const focusTarget = panelEl?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        focusTarget?.focus();
      }, 100);
    }
  }

  getErrorMessage(error: any) {
    switch (error.errorName) {
      case 'required':
        return this.generateMessage(error.businessName);
      default:
        return error.errorValue || error.errorName;
    }
  }

  generateMessage(businessName: string) {
    if (businessName == 'Social Security Number') {
      return `Please add your Social Security Number under the 'Identifiers' section in the 'Basic Information' tab before completing this form.`;
    }

    if (businessName == 'Date of Birth') {
      return `Please add Date of Birth to your profile under 'Demographics' in 'Basic Information' tab before completing this form.`;
    }

    return `${businessName} is required`;
  }

  showMailAllDialog() {
    this.showConfirmationDialog = true
  }

  handleConfirmMailAll() {
    const entityId = this.userService.getSelectedLead()!;
    const selectedProgram = this.program.program_code;
    const entityType = this.isParticipantFlow ? 'participant' : 'provider'

    this.showConfirmationDialog = false
    // TODO: Add proper types
    const formsToMail: SendMailFormPostItem[] = []

    this.checkListData.forEach(checklist => {
      checklist.items.forEach(item => {
        if (item.status !== "Completed" && item.formMasterId !== 50 && item.formMasterId !== 52) {
          formsToMail.push(
            {
              entityId,
              entityType,
              pplId: this.pplId || this.associatedPPLId || "",
              formMasterId: item.formMasterId,
              checklistDetails: item.details
            }
          )
        }
      })
    })
    // TODO: Wrap in a try/catch, only show success alert on successful call
    if (formsToMail.length === 0) {
      this.notificationService.alert(
        'warning',
        'Warning',
        'There are no forms to mail.'
      );
      return
    }
    this.mailFormService.sendMailForms(formsToMail, selectedProgram, entityId, entityType).subscribe(() => {
      this.notificationService.alert(
        'success',
        'Success',
        'All forms were successfully staged for mailing!'
      );
      this.mailFormService.triggerRefresh();
    });

  }

  handleRequestMailClick(formData: ChecklistItem | null) {
    if (!formData) return;
    const entityId = this.userService.getSelectedLead()!;
    const selectedProgram = this.program.program_code;
    const entityType = this.isParticipantFlow ? 'participant' : 'provider'
    this.selectedItemToMail.set(null);
    if (formData.formMasterId === 50 || formData.formMasterId === 52) {
      this.notificationService.alert(
        'warning',
        'Warning',
        'This form type is not supported for mailing.'
      );
      return
    }
    const formToMail: SendMailFormPostItem[] = [
      {
        entityId,
        entityType,
        pplId: this.pplId || this.associatedPPLId || "",
        formMasterId: formData.formMasterId,
        checklistDetails: formData.details
      }
    ]
    this.mailFormService.sendMailForms(formToMail, selectedProgram, entityId, entityType).subscribe(() => {
      this.notificationService.alert(
        'success',
        'Success',
        'Form was successfully staged for mailing!'
      );
      this.mailFormService.triggerRefresh();
    });
  }

  onCloseDialog() {
    this.showConfirmationDialog = false;
    this.selectedItemToMail.set(null);
  }

  private schedulePdfBlobCleanup(blobUrl?: string): void {
    if (!blobUrl?.startsWith('blob:')) {
      return;
    }

    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 0);
  }

  updateChecklistActivity(checklistGroup: any) {
    checklistGroup.isActive = !checklistGroup.isActive;
  }
}